import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import parsePhoneNumber from "libphonenumber-js";
import { InputPhone } from "./input-phone";

type PhoneInputAdapterProps = {
  name: string;
  handleFocus?: () => void;
  rules?: any;
  placeholder?: string;
  className?: string;
};

/**
 * PhoneInputAdapter - A wrapper component that adapts the new Nyxb UI InputPhone component
 * to work with the existing form structure and API expectations.
 * 
 * This component:
 * 1. Maintains backward compatibility with the old PhoneInput
 * 2. Handles the conversion between E.164 format and separate fields
 * 3. Ensures the API receives data in the expected format
 */
export function PhoneInputAdapter({
  name,
  handleFocus,
  rules,
  placeholder = "Phone number",
  className,
}: PhoneInputAdapterProps) {
  const { control, watch, setValue, setError, clearErrors, formState } = useFormContext();
  
  // Get the current value and calling code from form state
  const phoneNumber = watch(name);
  const callingCodePath = name.replace(/\.number$/, '.callingCode');
  const callingCode = watch(callingCodePath);
  
  // Track if we've shown validation errors
  const [hasValidated, setHasValidated] = React.useState(false);
  
  // Initialize with US country code if none is set but we have a phone number
  React.useEffect(() => {
    if (phoneNumber && !callingCode) {
      setValue(callingCodePath, { 
        value: { 
          telephoneCode: "1",
          alpha2code: "US" 
        } 
      });
    }
  }, [phoneNumber, callingCode, callingCodePath, setValue]);
  
  // Construct the full E.164 phone number for the InputPhone component
  const fullPhoneValue = phoneNumber && callingCode ? 
    `+${callingCode.value.telephoneCode}${phoneNumber}` : 
    '';
  
  // Handler for when the phone number changes in the InputPhone component
  const handlePhoneChange = (val: string) => {
    if (val) {
      try {
        // Parse the E.164 number to extract the national number
        const parsed = parsePhoneNumber(val);
        if (parsed) {
          // Set just the national number in the form state (without country code)
          // This maintains compatibility with the existing API
          setValue(name, parsed.nationalNumber);
          
          // Update the country code if detected from the phone number
          if (parsed.country) {
            setValue(callingCodePath, { 
              value: { 
                telephoneCode: parsed.countryCallingCode,
                alpha2code: parsed.country 
              } 
            });
          }
        }
      } catch (e) {
        // If parsing fails, just set the raw value
        setValue(name, val.replace(/^\+/, ''));
      }
    } else {
      // Clear the value if empty
      setValue(name, '');
    }
  };

  React.useEffect(() => {
    if (!phoneNumber) return; 
    const digitsOnly = phoneNumber.replace(/\D/g, ''); 
    if (digitsOnly.length > 0 && digitsOnly.length < 10) {
      setError(name, { message: 'Phone number must be at least 10 digits' });
    } else {
      clearErrors(name);
    }
  }, [phoneNumber, name, setError, clearErrors]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rules,
        validate: (value) => {      
          if (rules?.required && (!value || value.trim() === '')) {
            return 'Number is required';
          }
          if (value) {
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length > 0 && digitsOnly.length < 10) {
              return 'Phone number must be at least 10 digits';
            }
          }          
          return true;
        }
      }}
      render={({ field }) => (
        <InputPhone
          value={fullPhoneValue}
          onChange={handlePhoneChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={className}
        />
      )}
    />
  );
}
